import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, InputBase, makeStyles, Popper } from '@material-ui/core';
import { EditorState } from 'draft-js';
import { AutoCompleteSelect, RichText } from '../../../../components';
import Dialog from '../../../../components/Dialog';
import { AddIcon, DropDownIcon, DropUpIcon } from '../../../../components/Icon';
import LongRemark from '../../../../components/LongRemark';
import EmptyBed from '../Icons/EmptyBed';
import { generateGrid, initColor } from '../../../../utils/newBedStatusUtil';
import AutoCompleteInput from '../../../../components/AutoCompleteInput';
import {
  BED_STATUS_REMARK_COLOR,
  NEW_BED_STATUS_CUBICLE_ITEM_LIST,
  NEW_BED_STATUS_LENGTH,
} from '../../../../comm/constant';
import useUpdateEffect from '../../../../utils/useUpdateEffect';
import { Checkbox } from '../../../DutyAssign/components/Attendance/Setting';
import { initValue } from '../../../../utils';
import CubicleRemarksPopup from './CubicleRemarksPopup';

const useStyles = makeStyles(() => ({
  title: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: '#666666',
  },
  star: {
    color: '#ff0000',
  },
  input: {
    width: '100%',
    height: '3.5rem',
    padding: '1.25rem',
    border: 'solid 1px #e4e7eb',
    borderRadius: '10px',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  },
  remarkInput: {
    width: '100%',
    height: '12.875rem',
    padding: '1.25rem',
    border: 'solid 1px #e4e7eb',
    borderRadius: '10px',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
    alignItems: 'flex-start',
    '& .MuiInputBase-inputMultiline': {
      height: '100% !important',
      overflow: 'auto !important',
    },
  },
  titleRemark: {
    color: '#c0c6cb',
    marginLeft: '0.2rem',
  },
  addRemarkBtn: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: '#538fff',
  },
  addIcon: {
    fontSize: '1.2rem',
    marginRight: '0.45rem',
  },
  border: {
    width: '0.063rem',
    height: '1.188rem',
    backgroundColor: '#e4e7eb',
  },
  palette: ({ form }) => ({
    width: '1.375rem',
    height: '1.375rem',
    minWidth: '1.375rem',
    minHeight: '1.375rem',
    borderRadius: '4px',
    border: 'solid 1px #e4e7eb',
    marginRight: '0.75rem',
    backgroundColor: initValue(BED_STATUS_REMARK_COLOR[form.remarkColor]?.bgColor, 'fff'),
  }),
  dropDownIcon: {
    color: '#538fff',
    fontSize: '0.75rem',
  },
  remarkColorPopup: {
    zIndex: '9999',
  },
  colourItem: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
    width: '100%',
    height: '2.875rem',
    backgroundColor: '#fff',
    display: 'flex',
    justifyContent: 'flex-start',
    paddingLeft: '1.563rem',
    '&:hover': {
      backgroundColor: '#f5faff',
    },
  },
  bedArrangement: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1rem',
    color: '#538fff',
  },
  content: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  },
}));

const CreateCubicle = ({ handleClose, handleCreateCubicle }) => {
  const [remarkAnchorEl, setRemarkAnchorEl] = useState(null);
  const [remarkPopupAnchorEl, setRemarkPopupAnchorEl] = useState(null);
  const [form, setForm] = useState({
    name: '',
    type: '',
    status: '',
    gender: '',
    color: '',
    remarks: '',
    remarkColor: 'White',
    bedArrangement: { column: '1', row: '1' },
    borderLine: 'hide',
    header: 'show',
  });
  const [type, setType] = useState('Cubicle');
  const classes = useStyles({ form });

  const NurseStation = useMemo(() => {
    return type === 'Nurse Station/Misc.';
  }, [type]);

  const Memo = useMemo(() => {
    //  切换到 memo type的时候初始化一下remark的格式
    setForm((o) => ({
      ...o,
      remarks: type === 'Memo' ? EditorState.createEmpty() : '',
    }));

    return type === 'Memo';
  }, [type]);

  const updateForm = useCallback(
    (type) => (e) => {
      switch (type) {
        case 'name':
          setForm((o) => ({ ...o, [type]: e.target.value }));
          break;
        case 'remarks':
          setForm((o) => ({ ...o, [type]: Memo ? e : e.target.value }));
          break;
        case 'type':
        case 'status':
        case 'gender':
        case 'color':
          setForm((o) => ({ ...o, [type]: e }));
          break;
        case 'remarkColor':
          return () => {
            setRemarkAnchorEl(null);
            setForm((o) => ({ ...o, [type]: e.value }));
          };
        default:
          break;
      }
    },
    [Memo]
  );

  const handleRemarkOpen = useCallback(
    (event) => {
      setRemarkAnchorEl(remarkAnchorEl ? null : event.currentTarget);
    },
    [remarkAnchorEl]
  );

  const handleBedArrangement = useCallback(
    ({ column, row }) =>
      () => {
        setForm((o) => {
          o.bedArrangement = { column: column + 1, row: row + 1 };
          return { ...o };
        });
      },
    []
  );

  const handleSelected = useCallback(
    ({ column, row }) => {
      return Boolean(form.bedArrangement.column >= column + 1 && form.bedArrangement.row >= row + 1);
    },
    [form.bedArrangement]
  );

  const handleSubmit = useCallback(() => {
    handleCreateCubicle({
      data: {
        ...form,
        type: type === 'Cubicle' ? form.type : type,
        beds: generateGrid(form.bedArrangement.row, form.bedArrangement.column),
      },
      width: 1241,
      height: 975,
    });
  }, [handleCreateCubicle, form, type]);

  const confirmDisable = useMemo(() => {
    if (NurseStation || Memo) {
      return !form.name;
    } else {
      return !(
        form.name &&
        form.gender &&
        Number(form.bedArrangement.column) > 0 &&
        Number(form.bedArrangement.row) > 0
      );
    }
  }, [form, NurseStation, Memo]);

  // 选择性别的时候，自动切换默认颜色
  useUpdateEffect(() => {
    setForm((o) => ({
      ...o,
      color: initColor(o.gender),
    }));
  }, [form.gender]);

  // remark popup 开关
  const handleRemarkPopup = useCallback(
    (event) => {
      setRemarkPopupAnchorEl(remarkPopupAnchorEl ? null : event.currentTarget);
    },
    [remarkPopupAnchorEl]
  );

  // 关闭remark popup
  const handleCloseRemarkPopup = useCallback(() => {
    setRemarkPopupAnchorEl(null);
  }, []);

  // 选择remark
  const handleSelectRemark = useCallback(
    (remark, color) => () => {
      setForm((o) => ({
        ...o,
        remarks: `${String(o.remarks)} ${String(remark)}`.slice(0, 99),
        remarkColor: color,
      }));
      setRemarkPopupAnchorEl(null);
    },
    []
  );

  // 选择是否显示border
  const handleSelectBorderLine = useCallback(() => {
    setForm((o) => ({
      ...o,
      borderLine: o.borderLine === 'show' ? 'hide' : 'show',
    }));
  }, []);

  // 选择是否显示header
  const handleSelectHeader = useCallback(() => {
    setForm((o) => ({
      ...o,
      header: o.header === 'show' ? 'hide' : 'show',
    }));
  }, []);

  // 如果是Nurse station，则默认show border
  useEffect(() => {
    if (NurseStation) {
      setForm((o) => ({
        ...o,
        borderLine: 'show',
      }));
    }
  }, [NurseStation]);

  return (
    <>
      <Dialog
        open={true}
        width={Memo ? '80vw' : '69rem'}
        title={
          <Box display="flex" alignItems="center" gridGap="0.5rem" width="18rem">
            Create
            <AutoCompleteSelect
              itemList={NEW_BED_STATUS_CUBICLE_ITEM_LIST.Cubicle}
              value={type}
              onChange={setType}
              style={{ width: '100%' }}
            />
          </Box>
        }
        cancel="Cancel"
        confirm="Create"
        cancelStyle={{ border: 'unset' }}
        dialogfootStyle={{ paddingTop: '1.5rem', borderTop: 'solid 1px #e4e7eb', marginTop: '1.875rem' }}
        handleCancel={handleClose}
        handleClose={handleClose}
        handleConfirm={handleSubmit}
        disabled={confirmDisable}
      >
        <Box display="flex" gridGap="2.5rem">
          {/* 第一列 */}
          <Box display="flex" flexDirection="column" gridGap="1.25rem" flexBasis={Memo ? '60%' : '50%'}>
            {/* 第一行 */}
            <Box display="flex" gridGap="1.25rem">
              <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="100%">
                <div className={classes.title}>
                  Name<span className={classes.star}>*</span>
                  <span className={classes.titleRemark}>(20 words limited) </span>
                </div>
                <InputBase
                  className={classes.input}
                  placeholder="Name"
                  inputProps={{ maxLength: NEW_BED_STATUS_LENGTH.CUBICLE_NAME }}
                  value={form.name}
                  onChange={updateForm('name')}
                />
              </Box>
            </Box>
            {/* 第二行 */}
            {type === 'Cubicle' && (
              <>
                <Box display="flex" gridGap="1.25rem">
                  <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
                    <Box display="flex">
                      <div className={classes.title}> Type </div>
                      <LongRemark
                        content="• For general cubicles, leave it blank.
• For adding a nurse station or other non-cubicle area, kindly select “Nurse Station/Misc.”.
• If there is no suitable type in the list, kindly input by free text
"
                      />
                    </Box>
                    <AutoCompleteInput
                      width="100%"
                      placeholder="Select Type"
                      freeSolo
                      itemList={NEW_BED_STATUS_CUBICLE_ITEM_LIST.CubicleType}
                      value={form.type}
                      onChange={updateForm('type')}
                      maxLength={NEW_BED_STATUS_LENGTH.CUBICLE_TYPE}
                    />
                  </Box>
                  <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
                    <Box display="flex">
                      <div className={classes.title}> Status </div>
                      <LongRemark
                        content="• You can select the status in the list or just leave it blank.
• If there is no suitable status in the list, kindly input by free text.
"
                      />
                    </Box>
                    <AutoCompleteInput
                      width="100%"
                      placeholder="Select Status"
                      itemList={NEW_BED_STATUS_CUBICLE_ITEM_LIST.CubicleStatus}
                      value={form.status}
                      freeSolo
                      onChange={updateForm('status')}
                      maxLength={NEW_BED_STATUS_LENGTH.CUBICLE_STATUS}
                    />
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gridGap="0.75rem">
                  <Checkbox check={form.header === 'hide'} handleChange={handleSelectHeader} />
                  <div className={classes.content}>Hide Cubicle Name, Type, Status and remark in header</div>
                </Box>
              </>
            )}
            {NurseStation && (
              <Box display="flex" gridGap="1.25rem">
                <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
                  <div className={classes.title}> Colour </div>
                  <AutoCompleteSelect
                    type="color"
                    width="100%"
                    placeholder="Select Colour"
                    itemList={NEW_BED_STATUS_CUBICLE_ITEM_LIST.CubicleColor}
                    value={form.color}
                    onChange={updateForm('color')}
                    colorIconBorder="solid 1px #e4e7eb"
                  />
                  <Box display="flex" alignItems="center" gridGap="0.75rem">
                    <Checkbox check={form.borderLine === 'show'} handleChange={handleSelectBorderLine} />
                    <div className={classes.content}>Show border line</div>
                  </Box>
                </Box>
              </Box>
            )}
            {Memo && (
              <Box display="flex" gridGap="1.25rem">
                <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
                  <Box display="flex">
                    <div className={classes.title}> Status </div>
                    <LongRemark
                      content="• You can select the status in the list or just leave it blank.
• If there is no suitable status in the list, kindly input by free text.
"
                    />
                  </Box>
                  <AutoCompleteInput
                    width="100%"
                    placeholder="Select Status"
                    itemList={NEW_BED_STATUS_CUBICLE_ITEM_LIST.CubicleStatus}
                    value={form.status}
                    freeSolo
                    onChange={updateForm('status')}
                    maxLength={NEW_BED_STATUS_LENGTH.CUBICLE_STATUS}
                  />
                </Box>
                <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
                  <div className={classes.title}> Colour </div>
                  <AutoCompleteSelect
                    type="color"
                    width="100%"
                    placeholder="Select Colour"
                    itemList={NEW_BED_STATUS_CUBICLE_ITEM_LIST.CubicleColor}
                    value={form.color}
                    onChange={updateForm('color')}
                    colorIconBorder="solid 1px #e4e7eb"
                  />
                  <Box display="flex" alignItems="center" gridGap="0.75rem">
                    <Checkbox check={form.borderLine === 'show'} handleChange={handleSelectBorderLine} />
                    <div className={classes.content}>Show border line</div>
                  </Box>
                </Box>
              </Box>
            )}
            {/* 第三行 */}
            <Box display="flex" gridGap="1.25rem">
              {!NurseStation && !Memo && (
                <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
                  <div className={classes.title}>
                    Gender <span className={classes.star}>*</span>
                  </div>
                  <AutoCompleteSelect
                    width="100%"
                    placeholder="Select Gender"
                    itemList={NEW_BED_STATUS_CUBICLE_ITEM_LIST.Gender}
                    value={form.gender}
                    onChange={updateForm('gender')}
                  />
                </Box>
              )}
              {!NurseStation && !Memo && (
                <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="50%">
                  <div className={classes.title}> Colour </div>
                  <AutoCompleteSelect
                    type="color"
                    width="100%"
                    placeholder="Select Colour"
                    itemList={NEW_BED_STATUS_CUBICLE_ITEM_LIST.CubicleColor}
                    value={form.color}
                    onChange={updateForm('color')}
                    colorIconBorder="solid 1px #e4e7eb"
                  />
                  <Box display="flex" alignItems="center" gridGap="0.75rem">
                    <Checkbox check={form.borderLine === 'show'} handleChange={handleSelectBorderLine} />
                    <div className={classes.content}>Show border line</div>
                  </Box>
                </Box>
              )}
            </Box>
            {/* 第四行 */}
            {!NurseStation && (
              <Box display="flex" gridGap="1.25rem">
                <Box display="flex" flexDirection="column" gridGap="0.75rem" flexBasis="100%">
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <div className={classes.title}>
                      Remarks
                      <span className={classes.titleRemark}>{`(${Memo ? 1000 : 100} words limited)`} </span>
                    </div>
                    <Box display="flex" alignItems="center" gridGap="0.969rem">
                      <ButtonBase className={classes.addRemarkBtn} onClick={handleRemarkPopup}>
                        <AddIcon className={classes.addIcon} />
                        Add Remarks
                      </ButtonBase>
                      <div className={classes.border} />
                      <ButtonBase onClick={handleRemarkOpen}>
                        <div className={classes.palette} />
                        {remarkAnchorEl ? (
                          <DropUpIcon className={classes.dropDownIcon} />
                        ) : (
                          <DropDownIcon className={classes.dropDownIcon} />
                        )}
                      </ButtonBase>
                    </Box>
                  </Box>
                  {Memo ? (
                    <RichText value={form.remarks} onChange={updateForm('remarks')} />
                  ) : (
                    <InputBase
                      className={classes.remarkInput}
                      placeholder="Add remarks here"
                      inputProps={{
                        maxLength: Memo ? NEW_BED_STATUS_LENGTH.MEMO_REMARKS : NEW_BED_STATUS_LENGTH.CUBICLE_REMARKS,
                      }}
                      value={form.remarks}
                      onChange={updateForm('remarks')}
                      multiline
                    />
                  )}
                </Box>
              </Box>
            )}
          </Box>
          <div className={classes.border} style={{ height: '34.813rem' }} />
          {/* 第二列 */}
          <Box display="flex" flexDirection="column" gridGap="1rem" flexBasis={Memo ? '40' : '50%'}>
            <Box display="flex">
              <div className={classes.title}>{NurseStation || Memo ? 'Size' : 'Bed Arrangement'}</div>
              <LongRemark content="Size" />
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              padding="0.625rem 1rem 1.313rem"
              bgcolor="#f1f5fb"
              borderRadius="8px"
              gridGap="0.75rem"
            >
              <div className={classes.bedArrangement}>{`${form.bedArrangement.column}x${form.bedArrangement.row}`}</div>
              <Box
                display="grid"
                gridGap="0.45rem"
                gridTemplateColumns="1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr"
              >
                {bedArray.map((item) => (
                  <ButtonBase onClick={handleBedArrangement(item)} key={JSON.stringify(item)}>
                    <EmptyBed selected={handleSelected(item)} popup />
                  </ButtonBase>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Dialog>

      {/* remark color popup */}
      <Popper
        className={classes.remarkColorPopup}
        open={Boolean(remarkAnchorEl)}
        anchorEl={remarkAnchorEl}
        placement="bottom-end"
        transition
      >
        <Box
          display="flex"
          flexDirection="column"
          width="10rem"
          border="solid 1px #e4e7eb"
          bgcolor="#fff"
          boxShadow="0 1px 6px 0 rgba(0, 0, 0, 0.12)"
          borderRadius="10px"
          padding="0.5rem 0"
        >
          {NEW_BED_STATUS_CUBICLE_ITEM_LIST.RemarkColor.map((item) => (
            <ButtonBase className={classes.colourItem} key={item.value} onClick={updateForm('remarkColor')(item)}>
              <div className={classes.palette} style={{ backgroundColor: item.color }} /> {item.text}
            </ButtonBase>
          ))}
        </Box>
      </Popper>

      <CubicleRemarksPopup
        anchorEl={remarkPopupAnchorEl}
        handleClose={handleCloseRemarkPopup}
        handleClick={handleSelectRemark}
      />
    </>
  );
};

const rows = 12;
const cols = 14;

const bedArray = Array.from({ length: rows * cols }, (_, index) => ({
  column: index % cols,
  row: Math.floor(index / cols),
}));

export default CreateCubicle;
